import React from "react";
import { useFeatureGate } from "../lib/useFeatureGate";
import { useSettings } from "../lib/useSettings";
import { Lock, ArrowLeft } from "lucide-react";
import { Link } from "react-router-dom";

/**
 * <FeatureGate>
 *
 * Composant tout-en-un qui rend ses enfants UNIQUEMENT si le module
 * DigiPilote correspondant est activé. Sinon affiche une page "Module
 * désactivé" élégante avec un bouton retour.
 *
 * Usage dans App.js :
 *   <Route path="/sport/boutique" element={
 *     <FeatureGate moduleKey="shop_enabled" moduleName="Boutique">
 *       <SportPublicLayout><SportBoutique/></SportPublicLayout>
 *     </FeatureGate>
 *   }/>
 *
 * Ou pour rendre silencieusement (sans page d'erreur) :
 *   <FeatureGate moduleKey="..." silent>...</FeatureGate>
 */
export default function FeatureGate({
  moduleKey,
  moduleName = "Cette fonctionnalité",
  dependencies = [],
  silent = false,
  fallback,
  children,
}) {
  const enabled = useFeatureGate(moduleKey, ...dependencies);
  const { settings } = useSettings();

  // Pas encore chargé : on rend rien pour éviter le flash de la page désactivée
  if (!settings) return null;

  if (enabled) return <>{children}</>;
  if (silent) return null;
  if (fallback) return fallback;

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-br from-slate-50 to-slate-100">
      <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8 text-center" data-testid="feature-gate-disabled">
        <div className="w-14 h-14 mx-auto rounded-full bg-slate-100 flex items-center justify-center mb-4">
          <Lock className="w-6 h-6 text-slate-400"/>
        </div>
        <h1 className="text-xl font-black text-slate-900 mb-2">Module désactivé</h1>
        <p className="text-sm text-slate-600 mb-5">
          {moduleName} n'est pas activé sur ce site. L'administrateur peut l'activer
          depuis la <strong>Console widgets</strong> (Pack Sport Club).
        </p>
        <Link
          to="/"
          className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-slate-900 text-white font-bold text-sm hover:bg-slate-700 transition"
          data-testid="feature-gate-home-link"
        >
          <ArrowLeft size={14}/> Retour à l'accueil
        </Link>
      </div>
    </div>
  );
}
